import React, { useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import wordConverter from "num-words";
import {
  Add,
  Cancel,
  Close,
  Dashboard,
  ReceiptLong,
  Save,
} from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import PageContainer from "@jumbo/components/PageContainer";
import SelectBox from "@jumbo/components/JumboSelectBox";
import LoadingButton from "@jumbo/components/JumboLoadingButton";
import Button from "@jumbo/components/JumboLoadingButton";
import { BILL, HOME } from "@jumbo/constants/routes";
import { BILL_TO, SHIP_TO } from "@jumbo/constants/invoice";
import Div from "@jumbo/shared/Div";
import { getPartiList } from "app/redux/slice/partiSlice";
import { partiType } from "@jumbo/constants/parti";
import { createBill, editBill, updateBill } from "app/redux/slice/billSlice";
import moment from "moment";
import { getTaxList } from "app/redux/slice/taxSlice";

const useStyles = makeStyles({
  headingContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  mainInfoContainer: {
    display: "flex",
    minHeight: "170px",
    justifyContent: "center",
    flexDirection: "column",
  },
  headingTitleContainer: {
    display: "flex",
    flexGrow: "wrap",
  },
  headingTitle: {
    minWidth: "80px",
  },
  headingContent: {
    fontWeight: "500",
  },
  qrContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  table: {
    minWidth: 650,
    "& .MuiTableCell-root": {
      borderLeft: "1px solid",
      borderBottom: "1px solid",
    },
  },
  particular: {
    minWidth: "200px",
  },
  amount: {
    minWidth: "100px",
    verticalAlign: "top",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "10px 0px",
  },
  margin: {
    "& > :first-child": {
      marginTop: "10px",
    },
    marginBottom: "10px",
  },
  alignTop: {
    verticalAlign: "top",
  },
  loadingButtonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
});

const validationSchema = yup.object({
  vendorId: yup.string().required("Please select vendor."),
  billNumber: yup.string().required("Please enter Bill number."),
  billDate: yup.string().required("Please select Bill date."),
  service: yup.array().of(
    yup.object().shape({
      textField: yup.string().required("Please enter service name"),
      hsn: yup.number().required("Please enter HSN/SAC").min(1),
      amount: yup.number().required("Please enter amount").min(1),
    })
  ),
});

const AddAndUpdateBill = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const vendorId = searchParams.get("vendor-id");
  const vendorData = useSelector((state) => state?.parti?.list);
  const taxData = useSelector((state) => state?.tax?.list);
  let sum = 0;
  let taxSum = 0;
  let serviceIds = [];

  const breadcrumbData = [
    {
      path: HOME,
      icon: <Dashboard />,
      label: "Dashboard",
    },
    {
      path: BILL,
      icon: <ReceiptLong />,
      label: "Bill",
    },
    {
      label: id ? "Update Bill" : "Add Bill",
    },
  ];

  const formik = useFormik({
    initialValues: {
      vendorId: "",
      vendorInformation: "",
      billDate: moment(new Date()).format("YYYY-MM-DD"),
      billNumber: "",
      service: id
        ? []
        : [
            {
              _id: "",
              textField: "",
              tax1: null,
              tax2: null,
              hsn: "",
              amount: 0,
            },
          ],
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
      const serviceArr = [];

      values.service?.forEach((val) => {
        const taxArr = [];
        ["tax1", "tax2"].forEach((taxField) => {
          if (val[taxField]) {
            taxArr.push({
              taxId: val[taxField]?._id,
              name: val[taxField]?.taxName,
              rate: val[taxField]?.taxRate,
              amount: val[taxField]?.amount,
            });
          }
        });
        serviceArr.push({
          serviceName: val.textField,
          hsn: val.hsn,
          amount: val.amount,
          tax: taxArr,
        });
      });
      setSubmitting(true);
      const newBillData = {
        vendorId: values.vendorId,
        irnNo: "12345",
        ackNo: 785362,
        ackDate: values.billDate,
        billNo: values.billNumber?.trim(),
        date: values.billDate,
        amount: sum + taxSum,
        services: serviceArr,
      };
      const response = await dispatch(
        id ? updateBill({ id, data: newBillData }) : createBill(newBillData)
      ).unwrap();
      if (response.status === 200) {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "success",
        });
        navigate(BILL);
        resetForm();
      } else {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "error",
        });
      }
      setSubmitting(false);
    },
  });

  const { values, setFieldValue, errors } = formik;
  const serviceLength = values?.service?.length;

  const handleChangeVendor = (evt) => {
    const information =
      vendorData?.length &&
      vendorData.find((item) => item?._id === evt.target.value);
    setFieldValue("vendorId", evt.target.value);
    setFieldValue("vendorInformation", information);
  };

  const handleChange = (index, field, value) => {
    let updatedFields = values.service;
    if (field === "tax1" || field === "tax2") {
      const data =
        taxData?.length && taxData?.find((item) => item?._id === value);
      const newData = {
        ...data,
        amount: (data.taxRate * (updatedFields[index].amount ?? 0)) / 100,
      };
      updatedFields[index][field] = newData;
    } else {
      if (field === "amount") {
        if (updatedFields[index].tax1)
          updatedFields[index].tax1.amount =
            (updatedFields[index].tax1.taxRate * value) / 100;
        if (updatedFields[index].tax2)
          updatedFields[index].tax2.amount =
            (updatedFields[index].tax2.taxRate * value) / 100;
      }
      updatedFields[index][field] = value;
    }
    setFieldValue("service", updatedFields);
  };

  const handleAddMore = () => {
    formik.setValues({
      ...formik.values,
      service: [...formik.values.service, {}],
    });
  };

  const handleDeleteField = (index) => {
    let updatedFields = values.service;
    updatedFields.splice(index, 1);
    setFieldValue("service", updatedFields);
  };

  serviceLength &&
    values?.service?.forEach((item) => {
      item?.tax?.length &&
        item?.tax.map((data) => {
          return !serviceIds?.length
            ? serviceIds.push(data?.taxName)
            : !serviceIds.includes(data?.taxName) &&
                serviceIds.push(data?.taxName);
        });
    });
  useEffect(() => {
    dispatch(getPartiList({ partiType: partiType.Vendor }));
    dispatch(getTaxList({}));
    if (id) {
      dispatch(editBill(id))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const billDetail = response?.data?.data?.billDetail;
            let serviceArr = [];
            setFieldValue(
              "billDate",
              moment(billDetail?.date).format("YYYY-MM-DD")
            );
            setFieldValue("billNumber", billDetail?.billNo);
            setFieldValue("vendorId", billDetail?.vendorId?._id);
            setFieldValue("vendorInformation", billDetail?.vendorId);

            billDetail?.services?.forEach((val) => {
              let obj = {
                amount: +val?.amount,
                hsn: val?.hsn,
                tax1: val?.tax?.[0]
                  ? {
                      amount: val?.tax?.[0]?.amount,
                      taxName: val?.tax?.[0]?.name,
                      taxRate: val?.tax?.[0]?.rate,
                      _id: val?.tax?.[0]?.taxId,
                    }
                  : null,
                tax2: val?.tax?.[1]
                  ? {
                      amount: val?.tax?.[1]?.amount,
                      taxName: val?.tax?.[1]?.name,
                      taxRate: val?.tax?.[1]?.rate,
                      _id: val?.tax?.[1]?.taxId,
                    }
                  : null,
                textField: val?.serviceName,
                _id: "",
              };
              serviceArr.push(obj);
            });
            setFieldValue("service", serviceArr);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id, setFieldValue]);

  useEffect(() => {
    if (vendorId) {
      setFieldValue("vendorId", vendorId);
      const data = vendorData.find((item) => item?._id === vendorId);
      if (data) setFieldValue("vendorInformation", data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorData, vendorId]);

  const { shippingAddress, billingAddress, companyName, gstNo } =
    values?.vendorInformation;
  const { handleSubmit } = formik;
  return (
    <PageContainer
      heading={id ? "Update Bill" : "Add Bill"}
      breadcrumb={breadcrumbData}
    >
      <Card>
        <CardContent>
          <FormikProvider value={formik}>
            <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item md={9} xs={12}>
                  <Grid className={classes?.headingContainer}>
                    <Typography variant="h3" gutterBottom>
                      Tax Bill
                    </Typography>
                  </Grid>
                  {/* <Grid className={classes?.mainInfoContainer}>
                    <Grid container mb={{ xs: 2, md: 0 }}>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          gutterBottom
                          className={classes?.headingTitleContainer}
                        >
                          <span className={classes?.headingTitle}>IRN :</span>
                          <span className={classes?.headingContent}>{IRN}</span>
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container mb={{ xs: 2, md: 0 }}>
                      <Grid>
                        <Typography
                          variant="body1"
                          gutterBottom
                          className={classes?.headingTitleContainer}
                        >
                          <span className={classes?.headingTitle}>
                            Ack No. :
                          </span>
                          <span className={classes?.headingContent}>
                            {ACK_NO}
                          </span>
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container mb={{ xs: 2, md: 0 }}>
                      <Grid>
                        <Typography
                          variant="body1"
                          gutterBottom
                          className={classes?.headingTitleContainer}
                        >
                          <span className={classes?.headingTitle}>
                            Ack Date :
                          </span>
                          <span className={classes?.headingContent}>
                            {ACK_DATE}
                          </span>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid> */}
                </Grid>
                {/* <Grid item md={3} xs={12}>
                  <Grid className={classes?.headingContainer}>
                    <Typography variant="h3" gutterBottom>
                      e-Bill
                    </Typography>
                  </Grid>
                  <Grid item xs={12} className={classes?.qrContainer}>
                    <Grid>
                      <img
                        src={`${ASSET_IMAGES}/qr-code.png`}
                        width={"170px"}
                        height={"170px"}
                        alt="QR"
                      />
                    </Grid>
                  </Grid>
                </Grid> */}
              </Grid>
              <Grid container border={1}>
                <Grid item sm={6} xs={12} borderRight={{ xs: 0, sm: 1 }}>
                  <Grid>
                    <Grid px={1} pt={1} mb={1}>
                      <SelectBox
                        data={vendorData}
                        name="vendorId"
                        size="small"
                        labelKey="name"
                        valueKey="_id"
                        required
                        label="Select Vendor"
                        onChange={(evt) => handleChangeVendor(evt)}
                        value={values?.vendorId}
                        helperText={errors?.vendorId}
                      />
                    </Grid>
                    {values?.vendorInformation && (
                      <>
                        <Grid borderBottom={1} px={1}>
                          <Typography variant="body1" gutterBottom>
                            {SHIP_TO}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            {companyName}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {shippingAddress?.address1},
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {shippingAddress?.address2},
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {shippingAddress?.city},
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            GSTIN/UIN : {gstNo},
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            State Name : {shippingAddress?.state}, Code :{" "}
                            {shippingAddress?.pinCode}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Grid container>
                    <Grid
                      item
                      sm={6}
                      xs={12}
                      borderRight={{ xs: 0, sm: 1 }}
                      borderBottom={1}
                      p={1}
                    >
                      <TextField
                        label={"Bill No."}
                        name="billNumber"
                        fullWidth
                        size="small"
                        value={values?.billNumber}
                        onChange={(evt) =>
                          setFieldValue("billNumber", evt.target.value)
                        }
                        error={errors?.billNumber}
                        helperText={errors?.billNumber}
                      />
                    </Grid>
                    <Grid item sm={6} xs={12} borderBottom={1} p={1}>
                      <TextField
                        label={"Date"}
                        name="billDate"
                        fullWidth
                        size="small"
                        type="date"
                        onChange={(evt) =>
                          setFieldValue("billDate", evt.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        value={values?.billDate}
                        error={errors?.billDate}
                        helperText={errors?.billDate}
                      />
                    </Grid>

                    {values?.vendorInformation && (
                      <>
                        <Grid px={1} borderBottom={{ sm: 0, xs: 1 }}>
                          <Typography variant="body1" gutterBottom>
                            {BILL_TO}
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            {companyName}
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {billingAddress?.address1},
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {billingAddress?.address2},
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            {billingAddress?.city},
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            GSTIN/UIN : {gstNo},
                          </Typography>
                          <Typography variant="body1" gutterBottom>
                            State Name : {billingAddress?.state}, Code :{" "}
                            {billingAddress?.pinCode}
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} borderRight={1} borderBottom={1}>
                <TableContainer component={Paper}>
                  <Table
                    sx={{ minWidth: 650 }}
                    size="small"
                    aria-label="a dense table"
                    className={classes?.table}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Sr. No.</TableCell>
                        <TableCell className={classes?.particular}>
                          Particular
                        </TableCell>
                        <TableCell>HSN/SAC</TableCell>
                        <TableCell>Per (%)</TableCell>
                        <TableCell className={classes?.amount}>
                          Amount
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {serviceLength &&
                        values?.service.map((item, index) => {
                          sum += item?.amount ? +item?.amount : 0;
                          if (item.tax1) taxSum += item.tax1.amount ?? 0;
                          if (item.tax2) taxSum += item.tax2.amount ?? 0;
                          return (
                            <TableRow>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell className={classes?.particular}>
                                <Div className={classes?.margin} key={index}>
                                  <TextField
                                    label="Service Name"
                                    name={`service[${index}].textField`}
                                    fullWidth
                                    value={item?.textField}
                                    size="small"
                                    onChange={(evt) =>
                                      handleChange(
                                        index,
                                        "textField",
                                        evt.target.value
                                      )
                                    }
                                    error={
                                      formik?.errors?.service?.length &&
                                      formik?.errors?.service[index]?.textField
                                    }
                                    helperText={
                                      formik?.errors?.service?.length &&
                                      formik?.errors?.service[index]?.textField
                                    }
                                  />
                                </Div>
                                <Div className={classes?.margin} key={index}>
                                  <SelectBox
                                    data={taxData}
                                    id="tax1"
                                    name={`service[${index}].tax1`}
                                    size="small"
                                    labelKey="taxName"
                                    valueKey="_id"
                                    label="Select tax 1"
                                    required
                                    value={item?.tax1?._id}
                                    onChange={(evt) =>
                                      handleChange(
                                        index,
                                        "tax1",
                                        evt.target.value
                                      )
                                    }
                                    helperText={
                                      formik?.errors?.service?.length &&
                                      formik?.errors?.service[index]?.tax1
                                    }
                                  />
                                </Div>
                                <Div className={classes?.margin} key={index}>
                                  <SelectBox
                                    data={taxData}
                                    id="tax2"
                                    name={`service[${index}].tax2`}
                                    size="small"
                                    labelKey="taxName"
                                    valueKey="_id"
                                    label="Select tax 2"
                                    required
                                    value={item?.tax2?._id}
                                    onChange={(evt) =>
                                      handleChange(
                                        index,
                                        "tax2",
                                        evt.target.value
                                      )
                                    }
                                    helperText={
                                      formik?.errors?.service?.length &&
                                      formik?.errors?.service[index]?.tax2
                                    }
                                  />
                                </Div>
                              </TableCell>

                              <TableCell className={classes?.alignTop}>
                                <TextField
                                  placeholder="HSN/SAC"
                                  name={`hsn[${index}]`}
                                  fullWidth
                                  value={item?.hsn}
                                  size="small"
                                  onChange={(evt) =>
                                    handleChange(
                                      index,
                                      "hsn",
                                      +evt.target.value
                                    )
                                  }
                                  error={
                                    formik?.errors?.service?.length &&
                                    formik?.errors?.service[index]?.hsn
                                  }
                                  helperText={
                                    formik?.errors?.service?.length &&
                                    formik?.errors?.service[index]?.hsn
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                -
                                <Div className={classes?.margin} key={index}>
                                  <TextField
                                    fullWidth
                                    value={item?.tax1?.taxRate}
                                    size="small"
                                    disabled
                                  />
                                </Div>
                                <Div className={classes?.margin} key={index}>
                                  <TextField
                                    fullWidth
                                    value={item?.tax2?.taxRate}
                                    size="small"
                                    disabled
                                  />
                                </Div>
                              </TableCell>
                              <TableCell className={classes?.amount}>
                                <TextField
                                  label={"Amount"}
                                  name={`amount[${index}]`}
                                  fullWidth
                                  size="small"
                                  value={item?.amount}
                                  onChange={(evt) =>
                                    handleChange(
                                      index,
                                      "amount",
                                      +evt.target.value
                                    )
                                  }
                                  error={
                                    errors?.service?.length &&
                                    errors?.service[index]?.amount
                                  }
                                  helperText={
                                    errors?.service?.length &&
                                    errors?.service[index]?.amount
                                  }
                                />
                                <Div className={classes?.margin} key={index}>
                                  <TextField
                                    fullWidth
                                    value={item?.tax1?.amount}
                                    size="small"
                                    disabled
                                  />
                                </Div>
                                <Div className={classes?.margin} key={index}>
                                  <TextField
                                    fullWidth
                                    value={item?.tax2?.amount}
                                    size="small"
                                    disabled
                                  />
                                </Div>
                              </TableCell>

                              <TableCell>
                                {index !== 0 && (
                                  <IconButton
                                    aria-label="delete"
                                    color="secondary"
                                    onClick={() => handleDeleteField(index)}
                                  >
                                    <Cancel />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}

                      {serviceLength && (
                        <TableRow>
                          <TableCell />
                          <TableCell className={classes?.headingContent}>
                            Total
                          </TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell
                            colSpan={2}
                            className={classes?.headingContent}
                          >
                            {sum ? sum + taxSum : 0}
                          </TableCell>
                        </TableRow>
                      )}

                      <TableRow>
                        <TableCell colSpan={9}>
                          <Grid className={classes?.buttonContainer}>
                            <Button
                              type="button"
                              buttonLabel={"Add"}
                              sx={{ marginRight: "10px" }}
                              startIcon={<Add />}
                              variant="outlined"
                              onClick={handleAddMore}
                            />
                          </Grid>
                        </TableCell>
                      </TableRow>

                      {serviceLength && (
                        <TableRow>
                          <TableCell colSpan={9}>
                            <Grid container>
                              <Grid item sm={11}>
                                <Typography variant="body1">
                                  Amount Chargeable (in words)
                                </Typography>
                              </Grid>
                              <Grid item sm={1}>
                                <Typography variant="body1">
                                  E. & O.E
                                </Typography>
                              </Grid>
                            </Grid>
                            <Typography variant="subtitle2">
                              {wordConverter(
                                Math.round(sum ? sum + taxSum : 0)
                              )}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <br></br>
              <Grid container spacing={3}>
                <Grid
                  item
                  sm={12}
                  xs={12}
                  className={classes?.loadingButtonContainer}
                >
                  <LoadingButton
                    type="submit"
                    buttonLabel={id ? "Update" : "Save"}
                    sx={{ marginRight: "10px" }}
                    startIcon={<Save />}
                    variant="outlined"
                  />
                  <LoadingButton
                    color={"secondary"}
                    buttonLabel={"Close"}
                    variant="outlined"
                    startIcon={<Close />}
                    onClick={() => navigate(BILL)}
                  />
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default AddAndUpdateBill;
